<template>
  <div :style="popUpBorderStyle" class="score-container">
    <div>
      <p class="score-container--header">{{ headerMsg }}</p>
      <p
        :class="{
          'score--passed': quizResults.passed,
          'score--failed': !quizResults.passed,
        }"
        class="score"
      >
        {{ scoreMsg }}
      </p>
      <p class="instructions">
        {{ instructionMsg }}
      </p>
    </div>
    <div class="btn-container">
      <large-button
        v-if="leftBtn.route"
        :showArrow="false"
        :routeTo="leftBtn.route"
        class="btn"
      >
        {{ leftBtn.text }}
      </large-button>
      <large-button
        v-else-if="leftBtn.handleClick"
        :showArrow="false"
        @click.native="leftBtn.handleClick"
        class="btn"
      >
        {{ leftBtn.text }}
      </large-button>
      <large-button
        v-if="!isFirstQuiz && !quizResults.passed"
        :showArrow="false"
        @click.native="middleBtn.handleClick"
        class="btn"
      >
        {{ middleBtn.text }}
      </large-button>
      <large-button
        v-if="rightBtn.route"
        primary
        :showArrow="false"
        :routeTo="rightBtn.route"
        class="btn"
      >
        {{ rightBtn.text }}
      </large-button>
      <large-button
        v-else-if="rightBtn.handleClick"
        primary
        :showArrow="false"
        @click.native="rightBtn.handleClick"
        class="btn"
      >
        {{ rightBtn.text }}
      </large-button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Case from 'case'
import LargeButton from '@/components/LargeButton.vue'

export default {
  props: {
    quizResults: { type: Object, required: true },
    quizLength: { type: Number, required: true },
    reloadQuiz: { type: Function, required: true },
    isFirstQuiz: { type: Boolean, required: true },
  },
  components: {
    LargeButton,
  },
  data() {
    return {
      scoreMsg: '',
      showRetakeQuiz: false,
      headerMsg: '',
      instructionMsg: '',
      category: '',
      leftBtn: { text: '', route: '', handleClick: undefined },
      rightBtn: { text: '', route: '', handleClick: undefined },
      popUpBorderStyle: {},
      scoreStyle: {},
    }
  },
  mounted() {
    const subject = Case.camel(this.$route.params.category)
    this.category = subject
    const isTrainingSubject = this.quizResults.isTrainingSubject

    if (this.quizResults.passed) {
      this.headerMsg = this.$root.$t('quiz.result.passed.header')

      this.instructionMsg = this.isFirstQuiz
        ? this.$root.$t('quiz.result.passed.instructionMessage.isFirstQuiz')
        : isTrainingSubject
        ? this.$root.$t(
            'quiz.result.passed.instructionMessage.isTrainingSubject'
          )
        : this.$root.$t('quiz.result.passed.instructionMessage.else')
      this.leftBtn = {
        text: this.$root.$t('quiz.result.passed.leftButton'),
        handleClick: this.showReview,
      }
      this.rightBtn = {
        text: this.$root.$t('quiz.result.passed.rightButton'),
        route: `${this.$i18n.locale}/training`,
      }
      if (this.isFirstQuiz)
        this.rightBtn = {
          text: this.$root.$t('quiz.result.passed.rightButtonIsFirstQuiz'),
          route: `${this.$i18n.locale}/dashboard`,
        }
    } else {
      this.headerMsg = this.$root.$t('quiz.result.notPassed.header')
      this.instructionMsg = isTrainingSubject
        ? this.$root.$t(
            'quiz.result.notPassed.instructionMessage.isTrainingSubject'
          )
        : this.$root.$t('quiz.result.notPassed.instructionMessage.else')
      this.leftBtn = {
        text: this.$root.$t('quiz.result.notPassed.leftButton'),
        route: isTrainingSubject
          ? `${this.$i18n.locale}/training/course/${Case.kebab(this.category)}`
          : `${this.$i18n.locale}/training/review/${Case.kebab(this.category)}`,
      }
      this.middleBtn = {
        text: this.$root.$t('quiz.result.notPassed.middleButton'),
        handleClick: this.showReview,
      }
      if (this.isFirstQuiz)
        this.rightBtn = {
          text: this.$root.$t('quiz.result.notPassed.rightButtonIsFirstQuiz'),
          handleClick: this.showReview,
        }
      else
        this.rightBtn = {
          text: this.$root.$t('quiz.result.notPassed.rightButton'),
          handleClick: this.reloadQuiz,
        }
    }

    this.scoreMsg = this.$root.$t('quiz.result.scoreMessage', {
      quizResultsScore: this.quizResults.score,
      quizLength: this.quizLength,
    })
  },
  methods: {
    showReview() {
      this.$emit('showReview')
    },
  },
  computed: {
    ...mapState({
      subjects: state => state.subjects.subjects,
      training: state => state.subjects.training,
      user: state => state.user.user,
    }),
  },
}
</script>

<style lang="scss" scoped>
.btn {
  margin-top: 1em;
  width: 100px;

  @include breakpoint-above('tiny') {
    width: 150px;
  }

  @include breakpoint-above('small') {
    width: initial;
  }

  @include breakpoint-above('medium') {
    margin-bottom: 0;
    margin-left: 1em;
  }

  @include breakpoint-above('large') {
    margin-bottom: 0;
    margin-left: 2em;
  }
}

.btn-container {
  @include flex-container(row, space-around, center);
  width: 100%;
  margin-top: 4em;

  @include breakpoint-above('medium') {
    @include flex-container(row, flex-end, center);
  }
}

.score {
  margin-top: 1.25em;
  font-weight: 600;

  &--passed {
    color: $c-success-green;
  }

  &--failed {
    color: $c-error-red;
  }
}

.score-container {
  margin-top: 1em;

  &--header {
    font-weight: 500;
  }
}

@media screen and (max-width: 890px) {
  .score-container {
    width: 100%;
    left: 0;
  }
}

html.is-rtl {
  .btn {
    @include breakpoint-above('medium') {
      margin-right: 1em;
      margin-left: unset;
    }

    @include breakpoint-above('large') {
      margin-right: 2em;
      margin-left: unset;
    }
  }

  @media screen and (max-width: 890px) {
    .score-container {
      right: 0;
      left: unset;
    }
  }
}
</style>
